import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';

import { ReactComponent as NotificationIcon } from '../../../assets/icons/notifications/notification.svg';
import { ReactComponent as NotificationRejectedIcon } from '../../../assets/icons/notifications/notification_rejected.svg';
import { useStyles } from './PushPermissionDialog.styles';

interface IPushPermissionDialogProps {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const PushPermissionDialog = (props: IPushPermissionDialogProps) => {
  const { open, isLoading, onClose, onSubmit } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  const benefits = [
    t('settings.pushPermission.benefit1'),
    t('settings.pushPermission.benefit2'),
    t('settings.pushPermission.benefit3'),
  ];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && isLoading) {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown={isLoading}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.65)' } },
      }}
      PaperProps={{
        className: classes.paper,
        elevation: 8,
      }}>
      <IconButton
        type="button"
        className={classes.closeButton}
        onClick={handleClose}
        disabled={isLoading}
        aria-label={t('common.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box className={classes.inner}>
        <Box className={classes.iconBadge}>
          <NotificationIcon aria-hidden />
        </Box>

        <Typography component="h2" className={classes.title}>
          {t('settings.pushPermission.title')}
        </Typography>

        <Typography className={classes.description} component="p">
          {t('settings.pushPermission.description')}
        </Typography>

        <Box component="ul" className={classes.bulletList}>
          {benefits.map((text, index) => (
            <Box component="li" key={index} className={classes.bulletItem}>
              <Box className={classes.bulletDot} component="span" />
              <Typography component="span" className={classes.bulletText}>
                {text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          type="button"
          fullWidth
          variant="contained"
          disableElevation
          className={classes.primaryButton}
          onClick={onSubmit}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <NotificationIcon aria-hidden />
            )
          }>
          {t('settings.pushPermission.allow')}
        </Button>

        <Button
          type="button"
          className={classes.secondaryButton}
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<NotificationRejectedIcon aria-hidden />}>
          {t('settings.pushPermission.notNow')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PushPermissionDialog;
